import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'

const BG_MAIN = '#002f4b'
const BG_LOGO = '#00213a'
const BG_HOVER = 'rgba(255, 255, 255, 0.1)'
const BG_SELECTED = 'rgba(255, 255, 255, 0.12)'
const BORDER = 'rgba(255, 255, 255, 0.1)'
const TEXT = '#ffffff'
const TEXT_MUTED = 'rgba(255, 255, 255, 0.6)'
const ATTENTION = '#f59e0b'
const ATTENTION_TEXT = '#1c1917'

// ─── Container ───────────────────────────────────────────────────────────────

export const SidebarContainer = styled.div<{ expanded: boolean }>`
  width: ${({ expanded }) => (expanded ? '180px' : '60px')};
  height: 100vh;
  background-color: ${BG_MAIN};
  color: ${TEXT};
  display: flex;
  flex-direction: column;
  align-items: ${({ expanded }) => (expanded ? 'flex-start' : 'center')};
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 999;
  flex-shrink: 0;
`

// ─── Logo ─────────────────────────────────────────────────────────────────────

export const LogoWrapper = styled.div`
  background-color: ${BG_LOGO};
  cursor: pointer;
  height: 72px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  overflow: hidden;
  flex-shrink: 0;
  border-bottom: 1px solid ${BORDER};
  position: relative;
`

// ─── Navigation ───────────────────────────────────────────────────────────────

export const NavigationMenu = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  width: 100%;
`

export const NavItem = styled.div<{
  selected?: boolean
  withBorderTop?: boolean
  expanded?: boolean
}>`
  padding: 12px 18px;
  width: 100%;
  min-height: 52px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;
  user-select: none;
  border-bottom: ${({ withBorderTop }) => (!withBorderTop ? `1px solid ${BORDER}` : 'none')};
  border-top: ${({ withBorderTop }) => (withBorderTop ? `1px solid ${BORDER}` : 'none')};
  transition: background-color 0.25s ease;
  background-color: ${({ selected }) => (selected ? BG_SELECTED : 'transparent')};
  position: relative;

  &:hover {
    background-color: ${BG_HOVER};
  }

  svg {
    color: ${TEXT_MUTED};
    transition: color 0.25s;
  }

  &:hover svg {
    color: ${TEXT};
  }
`

export const ItemText = styled.span<{ expanded: boolean; lineBreak?: boolean }>`
  margin-left: 12px;
  opacity: ${({ expanded }) => (expanded ? 1 : 0)};
  transition: opacity 0.4s ease;
  white-space: ${({ lineBreak }) => (lineBreak ? 'normal' : 'nowrap')};
  overflow: hidden;
  width: 120px;
  font-size: 13px;
  font-weight: 500;
  color: ${TEXT};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`

// ─── Attention badge ─────────────────────────────────────────────────────────

const badgePop = keyframes`
  0%   { transform: scale(0.4); opacity: 0; }
  60%  { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(1); }
`

const badgeRing = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.7); }
  70%  { box-shadow: 0 0 0 7px rgba(245, 158, 11, 0); }
  100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
`

// Sits on the icon's top-right corner so it reads the same collapsed or
// expanded. Re-keyed on every count change, which replays the pop.
export const NavBadge = styled.span`
  position: absolute;
  top: 7px;
  left: 30px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  border: 2px solid ${BG_MAIN};
  background-color: ${ATTENTION};
  color: ${ATTENTION_TEXT};
  font-size: 10px;
  font-weight: 700;
  line-height: 14px;
  text-align: center;
  pointer-events: none;
  animation:
    ${badgePop} 0.35s cubic-bezier(0.34, 1.56, 0.64, 1),
    ${badgeRing} 1.6s ease-out 0.35s infinite;
`

// ─── Submenu ─────────────────────────────────────────────────────────────────

export const SubmenuContainer = styled.div`
  background-color: #001f35;
  border-radius: 8px;
  padding: 4px 0;
  min-width: 160px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
`

export const SubmenuItem = styled.div<{ clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  color: ${TEXT};
  font-size: 13px;
  transition: background-color 0.2s;

  svg {
    font-size: 17px;
    opacity: 0.7;
  }

  ${({ clickable }) =>
    clickable &&
    `
    &:hover {
      background-color: rgba(255, 255, 255, 0.08);
    }
  `}
`
